import React, { useState, useEffect, useRef } from 'react';
import { formatCurrency } from '../../utils/helpers';
import APIService from '../../services/api';
import { useAuth } from '../../hooks/useAuth';

const WebXPayModal = ({ 
  booking, 
  totalAmount, 
  currency = 'LKR', 
  onSuccess, 
  onClose, 
  onError 
}) => {
  const { state: authState } = useAuth();
  const { token, user } = authState;
  
  const [loading, setLoading] = useState(false);
  const [sessionReady, setSessionReady] = useState(false);
  const [sessionConfig, setSessionConfig] = useState(null);
  const [error, setError] = useState(null);
  const [savedCards, setSavedCards] = useState([]);
  const [showNewCard, setShowNewCard] = useState(true);
  const [saveCard, setSaveCard] = useState(false);
  const [show3DS, setShow3DS] = useState(false);
  const [threeDSHTML, setThreeDSHTML] = useState('');
  const [isInitializing, setIsInitializing] = useState(false); // 🆕 Prevent multiple inits
  
  const sessionIdRef = useRef(null);
  const scriptLoadedRef = useRef(false);
  const sessionConfiguredRef = useRef(false); // 🆕 Track if session is configured
  
  // Initialize session and load saved cards
  useEffect(() => {
    // Only initialize if we have a real booking ID (not temp one)
    if (booking?.id && !booking.id.toString().startsWith('temp_') && !isInitializing) {
      initializePaymentSession();
      if (token && user) {
        loadSavedCards();
      }
    } else if (booking?.id) {
      // If we have a temp booking ID, show message to create booking first
      setError('Please create booking first before initializing payment');
    }
    
    return () => {
      // Cleanup script if component unmounts
      cleanup();
    };
  }, [booking?.id]);

  const cleanup = () => {
    // Remove the PaymentSession script and global variables
    const script = document.querySelector('script[src*="webxpay"]');
    if (script) {
        script.remove();
    }
    // Reset references instead of touching window object
    scriptLoadedRef.current = false;
    sessionConfiguredRef.current = false;
    // Don't touch window.PaymentSession to avoid errors
    };

  // Initialize WebXPay session
  const initializePaymentSession = async () => {
    // 🆕 Prevent multiple simultaneous initializations
    if (isInitializing) {
      console.log('🔍 DEBUG: Already initializing, skipping...');
      return;
    }

    setIsInitializing(true);
    setLoading(true);
    setError(null);
    
    try {
      console.log('🔄 Initializing WebXPay session for booking:', booking);
      
      // 🔧 FIX: Use UUID field instead of potentially getting code
      const bookingUUID = booking.uuid || booking.bookingId || booking.id;
      
      if (!bookingUUID || bookingUUID.toString().startsWith('temp_')) {
        throw new Error('Valid booking UUID required');
      }
      
      console.log('📝 Using booking UUID:', bookingUUID);
      
      const sessionData = await APIService.initWebXPaySession(
        bookingUUID,  // 🔧 Use UUID instead of potentially code
        currency, 
        'visa_master'
      );
      
      console.log('✅ Session initialized:', sessionData);
      setSessionConfig(sessionData);
      
      // Load the hosted session iframe
      await loadHostedSessionScript(sessionData.hostedSessionUrl, sessionData.merchantId);
      
    } catch (err) {
      console.error('❌ Failed to initialize WebXPay session:', err);
      setError('Failed to initialize payment. Please try again.');
    } finally {
      setLoading(false);
      setIsInitializing(false);
    }
  };

  // Load saved cards for authenticated users
  const loadSavedCards = async () => {
    try {
      const cards = await APIService.getSavedCards(token);
      setSavedCards(cards || []);
      if (cards && cards.length > 0) {
        setShowNewCard(false); // Show saved cards first if available
      }
    } catch (err) {
      console.warn('Could not load saved cards:', err);
      // Not critical error, just continue with new card flow
    }
  };

  // Load the WebXPay hosted session script
  const loadHostedSessionScript = (hostedSessionUrl, merchantId) => {
    return new Promise((resolve, reject) => {
      console.log('🔍 DEBUG: Loading script from:', hostedSessionUrl);
      
      if (scriptLoadedRef.current && window.PaymentSession) {
        console.log('🔍 DEBUG: Script already loaded, setting up session...');
        setupPaymentSession(merchantId);
        resolve();
        return;
      }
      
      const script = document.createElement('script');
      script.src = hostedSessionUrl;
      script.async = true;
      
      script.onload = () => {
        console.log('📜 WebXPay script loaded successfully');
        console.log('🔍 DEBUG: PaymentSession available:', !!window.PaymentSession);
        scriptLoadedRef.current = true;
        setupPaymentSession(merchantId);
        resolve();
      };
      
      script.onerror = () => {
        console.error('❌ Failed to load WebXPay script from:', hostedSessionUrl);
        reject(new Error('Failed to load payment script'));
      };
      
      // Remove existing script if any
      const existingScript = document.querySelector('script[src*="webxpay"]');
      if (existingScript) {
        console.log('🔍 DEBUG: Removing existing script');
        existingScript.remove();
      }
      
      document.head.appendChild(script);
      console.log('🔍 DEBUG: Script added to head');
    });
  };

  // Setup payment session with iframe fields
  const setupPaymentSession = (merchantId) => {
    console.log('🔍 DEBUG: Setting up PaymentSession...');
    
    // 🆕 Prevent multiple configurations
    if (sessionConfiguredRef.current) {
      console.log('🔍 DEBUG: PaymentSession already configured, skipping...');
      return;
    }
    
    if (!window.PaymentSession) {
      console.error('❌ PaymentSession not available');
      setError('Payment system not available');
      return;
    }

    // 🔧 FIX: Wait for DOM elements to be ready
    setTimeout(() => {
      console.log('🔍 DEBUG: Checking if DOM elements exist...');
      const cardNumberEl = document.querySelector('#webxpay-card-number');
      const expiryMonthEl = document.querySelector('#webxpay-expiry-month');
      const expiryYearEl = document.querySelector('#webxpay-expiry-year');
      const cvvEl = document.querySelector('#webxpay-cvv');

      if (!cardNumberEl || !expiryMonthEl || !expiryYearEl || !cvvEl) {
        console.warn('⚠️ Card field elements not found in DOM yet, retrying...');
        // Retry after another delay
        setTimeout(() => setupPaymentSession(merchantId), 500);
        return;
      }

      console.log('✅ All card field elements found in DOM');

      try {
        console.log('🔍 DEBUG: Configuring PaymentSession with merchantId:', merchantId);
        
        // 🆕 Mark as being configured to prevent duplicates
        sessionConfiguredRef.current = true;
        
        window.PaymentSession.configure({
          fields: {
            card: {
              number: "#webxpay-card-number",
              expiryMonth: "#webxpay-expiry-month", 
              expiryYear: "#webxpay-expiry-year",
              securityCode: "#webxpay-cvv"
            }
          },
          frameEmbeddingMitigation: ["javascript"],
          callbacks: {
            initialized: (response) => {
              console.log('💳 Payment session initialized:', response);
              setSessionReady(true);
              console.log('✅ Session ready for user interaction');
            },
            formSessionUpdate: (response) => {
              console.log('🔄 Session update:', response);
              if (response.status === "ok" && response.session?.id) {
                sessionIdRef.current = response.session.id;
                setSessionReady(true);
                console.log('✅ Session ID ready:', response.session.id);
              } else {
                console.warn('⚠️ Session update failed:', response);
                setSessionReady(false);
              }
            },
            formSessionError: (error) => {
              console.error('❌ Payment session error:', error);
              setError('Payment form error. Please refresh and try again.');
            }
          }
        });
        
        console.log('⚙️ PaymentSession configured successfully');
      } catch (err) {
        console.error('❌ Failed to configure PaymentSession:', err);
        setError('Failed to setup payment form: ' + err.message);
        sessionConfiguredRef.current = false; // Reset on error
      }
    }, 100); // Small delay to ensure DOM is ready
  };

  // Process payment with new card
  const handlePayWithNewCard = async () => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('💳 Processing payment with new card...');
      console.log('🔍 Session ready state:', sessionReady);
      console.log('🔍 Session ID:', sessionIdRef.current);
      
      // 🔧 BETTER SESSION VALIDATION
      if (!sessionReady) {
        throw new Error('Payment form is not ready. Please wait for the form to load completely.');
      }
      
      if (!window.PaymentSession) {
        throw new Error('Payment system not available. Please refresh the page and try again.');
      }
      
      // Manually trigger session update when user wants to pay
      console.log('🔄 Updating session from form...');
      try {
        window.PaymentSession.updateSessionFromForm('card');
      } catch (sessionError) {
        console.error('❌ Session update failed:', sessionError);
        throw new Error('Failed to read card details. Please check your card information and try again.');
      }
      
      // 🔧 IMPROVED WAITING LOGIC - Check session ID with timeout
      let attempts = 0;
      const maxAttempts = 10; // 5 seconds total
      
      while (!sessionIdRef.current && attempts < maxAttempts) {
        console.log(`🔄 Waiting for session ID... attempt ${attempts + 1}`);
        await new Promise(resolve => setTimeout(resolve, 500));
        attempts++;
      }
      
      if (!sessionIdRef.current) {
        throw new Error('Payment session not ready. Please check your card details are filled correctly and try again.');
      }
      
      console.log('✅ Using session ID:', sessionIdRef.current);
      
      const bookingUUID = booking.uuid || booking.bookingId || booking.id;
      
      let result;
      
      if (saveCard && token) {
        result = await APIService.saveCardAndPay(
          sessionIdRef.current,
          bookingUUID,
          currency,
          totalAmount,
          token
        );
        console.log('💾 Save card and pay result:', result);
      } else {
        result = await APIService.processWebXPaySession(
          sessionIdRef.current,
          bookingUUID,
          currency,
          totalAmount
        );
        console.log('💰 One-time payment result:', result);
      }
      
      console.log('🔍 Checking payment result:', {
        requires3ds: result.requires3ds,
        hasHtml3ds: !!result.html3ds,
        hasHtml3dsUrl: !!result.html3dsUrl,
        success: result.success,
        bookingCode: result.bookingCode
      });

      if (result.requires3ds && (result.html3ds || result.html3dsUrl)) {
        console.log('🔐 3DS authentication required');
        
        if (result.html3dsUrl) {
          console.log('🔗 Opening 3DS URL in new window:', result.html3dsUrl);
          const popup = window.open(result.html3dsUrl, 'webxpay_3ds', 'width=600,height=700,scrollbars=yes,resizable=yes');
          
          if (popup) {
            setError('Please complete verification in the popup window. Return here when done.');
            
            const checkForCompletion = () => {
              try {
                const popupUrl = popup.location.href;
                if (popupUrl.includes('payment-success') || popupUrl.includes('payment-failed')) {
                  popup.close();
                  console.log('✅ 3DS completed, popup closed');
                  onSuccess({
                    bookingCode: result.bookingCode || booking.code,
                    paymentMethod: 'webxpay',
                    amount: totalAmount,
                    currency: currency
                  });
                }
              } catch (err) {
                if (popup.closed) {
                  console.log('✅ Popup closed by user or bank redirect');
                  onSuccess({
                    bookingCode: result.bookingCode || booking.code,
                    paymentMethod: 'webxpay',
                    amount: totalAmount,
                    currency: currency
                  });
                } else {
                  setTimeout(checkForCompletion, 2000);
                }
              }
            };
            
            setTimeout(checkForCompletion, 1000);
          } else {
            console.warn('⚠️ Popup blocked, trying modal fallback');
            if (result.html3ds) {
              setThreeDSHTML(result.html3ds);
              setShow3DS(true);
            }
          }
        } else if (result.html3ds) {
          console.log('🖼️ Showing 3DS in modal');
          setThreeDSHTML(result.html3ds);
          setShow3DS(true);
        }
      } else if (result.success || result.bookingCode) {
        console.log('✅ Payment successful (no 3DS required):', result.bookingCode || result);
        onSuccess({
          bookingCode: result.bookingCode || booking.code,
          paymentMethod: 'webxpay',
          amount: totalAmount,
          currency: currency,
          ...result
        });
      } else {
        const errorMessage = result.error || result.message || 'Payment failed - please try again';
        console.error('❌ Payment failed:', errorMessage);
        throw new Error(errorMessage);
      }
      
    } catch (err) {
      console.error('❌ Payment failed:', err);
      setError(err.message || 'Payment failed. Please check your card details and try again.');
    } finally {
      setLoading(false);
    }
  };

  // Pay with saved card
  const handlePayWithSavedCard = async (savedCardId) => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('💳 Processing payment with saved card:', savedCardId);
      
      // 🔧 FIX: Use UUID field consistently
      const bookingUUID = booking.uuid || booking.bookingId || booking.id;
      
      const result = await APIService.payWithSavedCard(
        savedCardId,
        bookingUUID,  // 🔧 Use UUID
        currency,
        totalAmount,
        token
      );
      
      console.log('💰 Saved card payment result:', result);
      
      if (result.requires3ds && result.html3ds) {
        console.log('🔐 3DS authentication required');
        setThreeDSHTML(result.html3ds);
        setShow3DS(true);
      } else if (result.success) {
        console.log('✅ Payment successful:', result.bookingCode);
        onSuccess(result);
      } else {
        throw new Error(result.error || 'Payment failed');
      }
      
    } catch (err) {
      console.error('❌ Saved card payment failed:', err);
      setError(err.message || 'Payment failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Close 3DS modal (payment completed via redirect)
  const handle3DSComplete = () => {
    setShow3DS(false);
    setThreeDSHTML('');
    // The 3DS flow will redirect to success/failure page
    // Or you can check payment status here
    onSuccess({ bookingCode: booking.code });
  };

  return (
    <>
      {/* Main WebXPay Modal - STYLED ONLY */}
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-lg w-full max-h-screen overflow-y-auto">
          {/* Header - STYLED ONLY */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <div>
              <h2 className="text-lg font-semibold">Complete Payment</h2>
              <p className="text-sm text-gray-600">{formatCurrency(totalAmount, currency)}</p>
            </div>
            <button 
              onClick={onClose}
              type="button"
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              disabled={loading}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="p-4 space-y-4">
            {/* Amount Summary - STYLED ONLY */}
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="flex justify-between items-center">
                <div>
                  <span className="text-xs text-gray-600">Total Amount</span>
                  <div className="text-xl font-bold text-green-600">
                    {formatCurrency(totalAmount, currency)}
                  </div>
                </div>
                <div className="text-right">
                  <span className="text-xs text-gray-500">Booking</span>
                  <div className="text-sm font-medium">{booking?.code}</div>
                </div>
              </div>
            </div>

            {/* Error Display */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-red-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-red-700 text-sm">{error}</span>
                </div>
              </div>
            )}

            {/* Loading State */}
            {loading && !sessionReady && (
              <div className="text-center py-8">
                <div className="inline-flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                  <span className="text-gray-600">Initializing payment system...</span>
                </div>
              </div>
            )}

            {/* Session Loading State */}
            {!loading && !sessionReady && !error && (
              <div className="text-center py-6">
                <div className="inline-flex flex-col items-center space-y-3">
                  <div className="animate-spin rounded-full h-6 w-6 border-2 border-gray-300 border-t-blue-600"></div>
                  <div className="text-gray-600 text-center">
                    <p className="font-medium text-sm">Setting up secure payment</p>
                    <p className="text-xs text-gray-500 mt-1">This may take a few moments...</p>
                  </div>
                </div>
              </div>
            )}

            {/* Browser Security Warning (localhost only) */}
            {window.location.protocol === 'http:' && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                <div className="flex items-start">
                  <svg className="w-4 h-4 text-yellow-500 mt-0.5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  <div className="text-yellow-800 text-xs">
                    <p className="font-medium">Development Mode</p>
                    <p className="mt-1">Browser security warnings are normal on localhost. Payment processing is still secure.</p>
                  </div>
                </div>
              </div>
            )}

            {/* Saved Cards Section */}
            {savedCards.length > 0 && !showNewCard && sessionReady && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium">Your Saved Cards</h3>
                <div className="space-y-3">
                  {savedCards.map((card) => (
                    <div
                      key={card.id}
                      className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-6 bg-gradient-to-r from-blue-500 to-purple-600 rounded text-white text-xs font-bold flex items-center justify-center">
                            {card.cardBrand === 'VISA' ? 'V' : 'MC'}
                          </div>
                          <div>
                            <div className="font-medium">{card.cardBrand}</div>
                            <div className="text-sm text-gray-500">•••• {card.cardLastFour}</div>
                            <div className="text-xs text-gray-400">{card.expiryMonth}/{card.expiryYear}</div>
                          </div>
                          {card.isDefault && (
                            <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                              Default
                            </span>
                          )}
                        </div>
                        <button
                          onClick={() => handlePayWithSavedCard(card.id)}
                          disabled={loading}
                          className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                        >
                          {loading ? 'Processing...' : `Pay ${formatCurrency(totalAmount, currency)}`}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                <button
                  onClick={() => setShowNewCard(true)}
                  className="w-full text-blue-600 hover:text-blue-700 py-2 text-sm font-medium transition-colors"
                >
                  + Use a different card
                </button>
              </div>
            )}

            {/* New Card Form - ONLY STYLING CHANGES */}
            {showNewCard && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium">Card Details</h3>
                  {savedCards.length > 0 && (
                    <button
                      onClick={() => setShowNewCard(false)}
                      className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                    >
                      ← Back to saved cards
                    </button>
                  )}
                </div>

                <div className="space-y-4">
                  {/* Card Number Field - MINIMAL STYLE CHANGES ONLY */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Card Number
                    </label>
                    <input
                      id="webxpay-card-number"
                      type="text"
                      readOnly
                      autoComplete="off"
                      style={{ 
                        width: '100%',
                        height: '36px', 
                        border: '1px solid #e5e7eb', 
                        borderRadius: '6px', 
                        padding: '8px 12px',
                        backgroundColor: 'white',
                        color: '#111827',
                        outline: 'none',
                        fontSize: '14px'
                      }}
                    />
                  </div>

                  {/* Expiry Date Fields - MINIMAL STYLE CHANGES ONLY */}
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Month
                      </label>
                      <input
                        id="webxpay-expiry-month"
                        type="text"
                        readOnly
                        autoComplete="off"
                        style={{ 
                          width: '100%',
                          height: '36px', 
                          border: '1px solid #e5e7eb', 
                          borderRadius: '6px', 
                          padding: '8px 12px',
                          backgroundColor: 'white',
                          color: '#111827',
                          outline: 'none',
                          fontSize: '14px',
                          textAlign: 'center'
                        }}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Year
                      </label>
                      <input
                        id="webxpay-expiry-year"
                        type="text"
                        readOnly
                        autoComplete="off"
                        style={{ 
                          width: '100%',
                          height: '36px', 
                          border: '1px solid #e5e7eb', 
                          borderRadius: '6px', 
                          padding: '8px 12px',
                          backgroundColor: 'white',
                          color: '#111827',
                          outline: 'none',
                          fontSize: '14px',
                          textAlign: 'center'
                        }}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        CVV
                      </label>
                      <input
                        id="webxpay-cvv"
                        type="text"
                        readOnly
                        autoComplete="off"
                        style={{ 
                          width: '100%',
                          height: '36px', 
                          border: '1px solid #e5e7eb', 
                          borderRadius: '6px', 
                          padding: '8px 12px',
                          backgroundColor: 'white',
                          color: '#111827',
                          outline: 'none',
                          fontSize: '14px',
                          textAlign: 'center'
                        }}
                      />
                    </div>
                  </div>

                  {/* Save Card Option for Authenticated Users */}
                  {token && user && (
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={saveCard}
                        onChange={(e) => setSaveCard(e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">
                        Save this card for future payments
                      </span>
                    </label>
                  )}

                  {/* Action Buttons - Added Cancel Button */}
                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={onClose}
                      type="button"
                      disabled={loading}
                      className="flex-1 py-4 border border-gray-200 rounded-lg font-semibold text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handlePayWithNewCard}
                      disabled={loading || !sessionReady}
                      className={`flex-[2] py-4 rounded-lg font-semibold transition-colors ${
                        loading || !sessionReady
                          ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                          : 'bg-green-600 hover:bg-green-700 text-white'
                      }`}
                    >
                      {loading 
                        ? 'Processing...' 
                        : `Pay ${formatCurrency(totalAmount, currency)}`
                      }
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Security Badges */}
            <div className="flex items-center justify-center space-x-3 text-xs text-gray-500">
              <div className="flex items-center space-x-1">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <span>Secured by WebXPay</span>
              </div>
              <span className="px-2 py-1 bg-green-100 text-green-800 rounded">SSL</span>
              <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded">256-bit</span>
              <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded">PCI DSS</span>
            </div>
          </div>
        </div>
      </div>

      {/* 3DS Authentication Modal */}
      {show3DS && threeDSHTML && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-60 p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">Verify Your Payment</h3>
                <button
                  onClick={handle3DSComplete}
                  className="p-2 hover:bg-gray-100 rounded-full"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <p className="text-sm text-gray-600 mt-1">
                Please complete the verification with your bank
              </p>
            </div>
            <div className="p-4">
              <div
                id="webxpay-3ds-container"
                className="w-full min-h-[400px] border rounded"
                dangerouslySetInnerHTML={{ __html: threeDSHTML }}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default WebXPayModal;